import React, { useEffect, useRef, useState } from 'react';
import { Box } from '@material-ui/core';
import { emailIsValidRe, getUserRoleList, wait } from 'globals/utils';
import defaultLocales from 'globals/locales/default';
import { ReactComponent as InviteNewUserIcon } from 'assets/images/invite-new-user/invite-new-user.svg';
import FsSimpleInput from 'ui-elements/form-elements/FsDefaultInput';
import FsPendingButton from 'ui-elements/buttons/FsPendingButton';
import FsFormFullWidthContainer from 'ui-elements/form-elements/FsFormFullWidthContainer';
import FsBody from 'ui-elements/typography/FsBody';
import FsCenterBox from 'ui-elements/common-elements/FsCenterBox';
import FsDefaultSelect from 'ui-elements/form-elements/FsDefaultSelect';
import FsCheckboxWithLabel from 'ui-elements/form-elements/FsCheckboxWithLabel';

export interface OnSuccessProps {
  email: string;
}

interface FsInviteNewUserFormProps {
  onSuccess?: ({ email }: OnSuccessProps) => void;
}

const FsInviteNewUserForm: React.FC<FsInviteNewUserFormProps> = ({
  onSuccess,
}) => {
  const formEl = useRef<HTMLFormElement>(null);
  const [email, setEmail] = useState<string>('');
  const [role, setRole] = useState<string>('');
  const [pending, setPending] = useState<boolean>(false);
  const [isFormValid, setIsFormValid] = useState<boolean>(false);
  const [isOtherUsers, setIsOtherUsers] = useState<boolean>(false);

  const fields = [
    {
      required: true,
      name: defaultLocales.email.toLowerCase(),
      value: email,
      label: defaultLocales.email,
      onChange: (e: React.ChangeEvent<HTMLInputElement>): void => {
        setEmail(e.target?.value);
      },
    },
    {
      required: true,
      isSelect: true,
      options: getUserRoleList(),
      name: defaultLocales.role.toLowerCase(),
      value: role,
      label: defaultLocales.role,
      onChange: (e: React.ChangeEvent<HTMLInputElement>): void => {
        setRole(e.target?.value);
      },
    },
    {
      name: 'other-users',
      checked: isOtherUsers,
      label: defaultLocales.canSeeOtherUsers,
      onChange: (e: React.ChangeEvent<HTMLInputElement>): void => {
        setIsOtherUsers(e.target?.checked);
      },
    },
  ];

  const resetForm = () => {
    setEmail('');
  };

  const handleSubmit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    setPending(true);
    // eslint-disable-next-line no-console
    console.log('Save changes', {
      email,
      role,
      isOtherUsers,
    });
    await wait(1000);
    setPending(false);
    if (onSuccess) onSuccess({ email });
    resetForm();
  };

  useEffect(() => {
    const isDefaultFormValid = !!formEl?.current?.checkValidity();
    const isEmailValid = !!email.match(emailIsValidRe);
    setIsFormValid(isDefaultFormValid && isEmailValid);
  }, [email, role, isOtherUsers]);

  return (
    <Box>
      <FsCenterBox marginBottom={4}>
        <InviteNewUserIcon />
      </FsCenterBox>

      <Box maxWidth={300} marginBottom={2}>
        <FsBody>{defaultLocales.invitationFormInstruction}</FsBody>
      </Box>

      <form onSubmit={handleSubmit} ref={formEl}>
        {fields.map(
          ({ name, isSelect, checked, options, ...otherProps }, idx) => (
            <FsFormFullWidthContainer
              key={name}
              style={{ marginBottom: idx === 1 ? 0 : undefined }}
            >
              {name === 'role' && isSelect && options && (
                <FsDefaultSelect
                  {...{ ...otherProps, name, options }}
                  fullWidth
                  fsVariant="outlined"
                />
              )}

              {name === 'other-users' && (
                <FsCheckboxWithLabel
                  {...{ ...otherProps, name, options, checked: !!checked }}
                />
              )}

              {name !== 'role' && name !== 'other-users' && (
                <FsSimpleInput
                  {...{ ...otherProps, name }}
                  fsVariant="outlined"
                  fullWidth
                />
              )}
            </FsFormFullWidthContainer>
          )
        )}
        <Box display="flex" flexDirection="row-reverse">
          <FsPendingButton
            type="submit"
            variant="contained"
            color="primary"
            pending={pending}
            width={160}
            disabled={!isFormValid}
          >
            {defaultLocales.invite}
          </FsPendingButton>
        </Box>
      </form>
    </Box>
  );
};

export default FsInviteNewUserForm;
